"use client";

import {useEffect, useState, useRef, useCallback} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import {DataTable} from "../../components/data-table";
import {SiteHeader} from "../../components/site-header";
import {BinUsageStats} from "./bin-usage-stats";
import {Button} from "@/components/ui/button";
import {useFilters} from "@/common/Components/SideBar/Layout";
import axiosInstance from "@/api/axios";
import {HeatMap} from "@/components";
import {WasteComposition} from "@/components/waste-composition";
import {BinMediaViewer} from "@/components/bin-images-viewer";
import {DashboardExport} from "@/components/DashboardExport";
import {useTranslation} from 'react-i18next';
import {
    WasteStreamProduction,
    Stats,
    BinUsageRanking,
    FillLevelConsistency,
    PeakUsageTime,
    PeakFillLevel,
} from "../../components";
import SubclassDisplay from "../../components/Subclass-Display/SubclassDisplay";
import ContaminationChart from "../../components/ContaminationChart";
import WasteCreated from "@/pages/Others/Components/WasteCreated";
import {initializeApp} from '@firebase/app';
import {getMessaging, getToken, onMessage} from '@firebase/messaging';
import SubclassCardView from "../../components/Subclass-Display/SubclassCardView";
import {ContaminationRateChart} from "../../components/ContaminationRateChart";
import {ContaminationTime} from "../../components/Contaminationtime";
import { WasteForecast } from "../../components/Wasteforecast";
import { ContaminationTimeSeries } from "../../components/Contaminationtimeseries";

const firebaseConfig = {
    apiKey: "AIzaSyBylDQErfG_E2F7LpO6boLYf4QMRwtek3g",
    authDomain: "plaex-notifications-8dfa0.firebaseapp.com",
    projectId: "plaex-notifications-8dfa0",
    storageBucket: "plaex-notifications-8dfa0.firebasestorage.app",
    messagingSenderId: "16520628768",
    appId: "1:16520628768:web:f3f43ec41ebaa3ba8f8a45"
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

const ADMIN_EMAILS = new Set([
    "tyrellpantophlet@gmail.com",
    "itsplaex@gmail.com",
]);

const getUserStorageKey = (key) => {
    const userKey = localStorage.getItem("userKey") || "default";
    return `plaex_${userKey}_${key}`;
};

const usePersistentBinSelection = () => {
    const storageKey = getUserStorageKey('selected_bins');

    const [selectedBins, setSelectedBins] = useState(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (e) {
            console.error('[Bins] Error reading storage:', e);
        }
        return [];
    });

    useEffect(() => {
        if (selectedBins.length === 0) return;
        try {
            localStorage.setItem(storageKey, JSON.stringify(selectedBins));
        } catch (e) {
            console.error('[Bins] Error saving to storage:', e);
        }
    }, [selectedBins, storageKey]);

    const toggleBinSelection    = (binId)     => setSelectedBins(prev => prev.includes(binId) ? prev.filter(id => id !== binId) : [...prev, binId]);
    const clearAllBins          = ()          => setSelectedBins([]);
    const selectAllBins         = (allBinIds) => setSelectedBins(allBinIds);
    const setDefaultBinsIfEmpty = (defaultBins) => setSelectedBins(prev => prev.length > 0 ? prev : defaultBins);

    return {selectedBins, toggleBinSelection, clearAllBins, selectAllBins, setDefaultBinsIfEmpty};
};

export default function Page() {
    const {
        selectedBins: persistentSelectedBins,
        toggleBinSelection: persistentToggleBinSelection,
        clearAllBins: persistentClearAllBins,
        selectAllBins: persistentSelectAllBins,
        setDefaultBinsIfEmpty,
    } = usePersistentBinSelection();

    const [userRoleState, setUserRoleState]   = useState(null);
    const [isLoadingRole, setIsLoadingRole]   = useState(true);
    const [hasBinImages, setHasBinImages]     = useState(false);
    const [binUsageIds, setBinUsageIds]       = useState(null);
    const [showBinUsageStats, setShowBinUsageStats] = useState(null);
    const [selectedYear, setSelectedYear]     = useState(new Date().getFullYear());
    const [refreshKey, setRefreshKey]         = useState(0);
    const [localTimeRangeDropdownOpen, setLocalTimeRangeDropdownOpen] = useState(false);
    const [localBinDropdownOpen, setLocalBinDropdownOpen]             = useState(false);
    const [contaminationExportData, setContaminationExportData] = useState(null);

    const [currentUserEmail, setCurrentUserEmail] = useState(() => {
        const stored = localStorage.getItem("userEmail") || localStorage.getItem("userKey") || "";
        return stored.toLowerCase().trim();
    });

    const isCurrentUserAdmin = ADMIN_EMAILS.has(currentUserEmail);

    const [localSelectedTimeRange, setLocalSelectedTimeRange] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserStorageKey('time_range'));
            return saved ? JSON.parse(saved) : "Year";
        } catch { return "Year"; }
    });

    const [localCustomDateRange, setLocalCustomDateRange] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserStorageKey('custom_date'));
            if (saved) {
                const parsed = JSON.parse(saved);
                return [{
                    startDate: parsed.startDate ? new Date(parsed.startDate) : null,
                    endDate:   parsed.endDate   ? new Date(parsed.endDate)   : null,
                    key: 'selection'
                }];
            }
        } catch {}
        return [{startDate: null, endDate: null, key: 'selection'}];
    });

    const [localIsCustomDateRange, setLocalIsCustomDateRange] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserStorageKey('custom_date'));
            if (saved) return JSON.parse(saved).isCustom || false;
        } catch {}
        return false;
    });

    const [localAllBins, setLocalAllBins] = useState(() => {
        try {
            const stored = localStorage.getItem(getUserStorageKey('all_bins'));
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });

    useEffect(() => {
        if (localAllBins.length === 0) return;
        try {
            localStorage.setItem(getUserStorageKey('all_bins'), JSON.stringify(localAllBins));
        } catch (e) {
            console.error('[Bins] Error saving allBins to storage:', e);
        }
    }, [localAllBins]);

    const [localWasteTypeDropdownOpen, setLocalWasteTypeDropdownOpen] = useState(false);
    const [localSelectedWasteTypes, setLocalSelectedWasteTypes]       = useState(['organic']);
    const [wasteCreatedKg, setWasteCreatedKg]   = useState(0);
    const [binTypes, setBinTypes]               = useState({});
    const [hasGarbyVisionBins, setHasGarbyVisionBins] = useState(false);
    const hasInitializedBins = useRef(false);

    // Chart flags
    const [showContaminationTimeFlag,       setShowContaminationTimeFlag]       = useState(false);
    const [showContaminationTimeSeriesFlag, setShowContaminationTimeSeriesFlag] = useState(false);
    const [showContaminationImagesFlag,     setShowContaminationImagesFlag]     = useState(false);
    const [showContaminationTimeImagesFlag, setShowContaminationTimeImagesFlag] = useState(false);


    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        selectedTimeRange: contextTimeRange,
        selectedBins: contextBins,
        userRole,
        selectedWasteTypes: contextWasteTypes,
        wasteTypeOptions: contextWasteTypeOptions,
        customDateRange: contextCustomDateRange,
        isCustomDateRange: contextIsCustomDateRange,
    } = useFilters();

    const selectedBins        = contextBins        || persistentSelectedBins;
    const selectedTimeRange   = contextTimeRange   || localSelectedTimeRange;
    const timeRangeOptions    = ["Day", "Week", "Month", "3 Month", "6 Month", "Year"];
    const selectedWasteTypes  = contextWasteTypes  || localSelectedWasteTypes;
    const wasteTypeOptions    = contextWasteTypeOptions || ['pmd', 'organic', 'paper', 'residual'];
    const customDateRange     = contextCustomDateRange  || localCustomDateRange;
    const isCustomDateRange   = contextIsCustomDateRange || localIsCustomDateRange;

    // Handler for contamination data from ContaminationChart
    const handleContaminationDataLoaded = useCallback((data) => {
        setContaminationExportData(data);
    }, []);

    // Fetch current user email once on mount
    useEffect(() => {
        axiosInstance.get("current-user/")
            .then((res) => {
                const email = (res.data?.email ?? "").toLowerCase().trim();
                if (email) {
                    setCurrentUserEmail(email);
                    localStorage.setItem("userEmail", email);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!contextTimeRange) {
            localStorage.setItem(getUserStorageKey('time_range'), JSON.stringify(localSelectedTimeRange));
        }
    }, [localSelectedTimeRange, contextTimeRange]);

    useEffect(() => {
        if (!contextCustomDateRange) {
            const toSave = {
                startDate: localCustomDateRange[0]?.startDate instanceof Date
                    ? localCustomDateRange[0].startDate.toISOString()
                    : localCustomDateRange[0]?.startDate || null,
                endDate: localCustomDateRange[0]?.endDate instanceof Date
                    ? localCustomDateRange[0].endDate.toISOString()
                    : localCustomDateRange[0]?.endDate || null,
                isCustom: localIsCustomDateRange,
            };
            localStorage.setItem(getUserStorageKey('custom_date'), JSON.stringify(toSave));
        }
    }, [localCustomDateRange, localIsCustomDateRange, contextCustomDateRange]);

    const handleCustomDateRangeChange = (newDateRange, isCustom) => {
        setLocalCustomDateRange(newDateRange);
        setLocalIsCustomDateRange(isCustom);
        if (!isCustom) localStorage.removeItem(getUserStorageKey('custom_date'));
    };

    const fetchBinTypes = async (bins) => {
        if (!bins || bins.length === 0) {
            setHasGarbyVisionBins(false);
            setBinTypes({});
            setShowContaminationTimeFlag(false);
            setShowContaminationTimeSeriesFlag(false);
            setShowContaminationImagesFlag(false);
            setShowContaminationTimeImagesFlag(false);
            return;
        }
        try {
            const response = await axiosInstance.get('/get_bins');
            const allBins  = response.data;
            const binTypeMap = {};
            let garbyVisionFound             = false;
            let contaminationTimeFound       = false;
            let contaminationTimeSeriesFound = false;
            let contaminationImagesFound     = false;
            let contaminationTimeImagesFound = false;

            bins.forEach(binId => {
                const bin = allBins.find(b => b.unique_identifier === binId);
                if (bin) {
                    binTypeMap[binId] = {
                        isGarbyVision:               bin.is_garby_vision,
                        isGarfill:                   bin.is_garfill,
                        isWeightScale:               bin.is_weight_scale,
                        showContaminationTime:        bin.show_contamination_time,
                        showContaminationTimeSeries:  bin.show_contamination_timeseries,
                        showContaminationImages:      bin.show_contamination_images,
                        showContaminationTimeImages:  bin.show_contamination_time_images,
                    };
                    if (bin.is_garby_vision)               garbyVisionFound             = true;
                    if (bin.show_contamination_time)       contaminationTimeFound       = true;
                    if (bin.show_contamination_timeseries) contaminationTimeSeriesFound = true;
                    if (bin.show_contamination_images)     contaminationImagesFound     = true;
                    if (bin.show_contamination_time_images) contaminationTimeImagesFound = true;
                }
            });

            setBinTypes(binTypeMap);
            setHasGarbyVisionBins(garbyVisionFound);
            setShowContaminationTimeFlag(contaminationTimeFound);
            setShowContaminationTimeSeriesFlag(contaminationTimeSeriesFound);
            setShowContaminationImagesFlag(contaminationImagesFound);
            setShowContaminationTimeImagesFlag(contaminationTimeImagesFound);
        } catch (error) {
            console.error("Error fetching bin types:", error);
            setHasGarbyVisionBins(false);
            setBinTypes({});
            setShowContaminationTimeFlag(false);
            setShowContaminationTimeSeriesFlag(false);
            setShowContaminationImagesFlag(false);
            setShowContaminationTimeImagesFlag(false);
        }
};

    useEffect(() => {
        fetchBinTypes(selectedBins);
    }, [selectedBins]);

    useEffect(() => {
        const savedRole = localStorage.getItem("userRole");
        if (savedRole) {
            setUserRoleState(savedRole);
            setIsLoadingRole(false);
        }
    }, []);

    useEffect(() => {
        const initializeNotifications = async () => {
            try {
                if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') return;
                const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                const currentToken   = await getToken(messaging, {serviceWorkerRegistration: swRegistration});
                if (currentToken) await sendTokenToServer(currentToken);
            } catch (error) {
                console.error('Error initializing notifications:', error);
            }
        };
        initializeNotifications();
    }, [navigate]);

    const sendTokenToServer = async (token) => {
        try {
            const csrfResponse = await axiosInstance.get("csrf/");
            const csrfToken    = csrfResponse.data.csrfToken;
            await axiosInstance.post('/fcm/devices/', {
                registration_id: token,
                type: 'web'
            }, {
                headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                withCredentials: true,
            });
        } catch (error) {
            console.error('Error sending FCM token:', error);
        }
    };

    useEffect(() => {
        if (userRole) {
            setUserRoleState(userRole);
            localStorage.setItem("userRole", userRole);
            setIsLoadingRole(false);
        } else if (!userRoleState) {
            const fetchUserRole = async () => {
                try {
                    const response = await axiosInstance.get("usertier");
                    setUserRoleState(response.data);
                    localStorage.setItem("userRole", response.data);
                } catch (error) {
                    console.error("Error fetching user role:", error);
                } finally {
                    setIsLoadingRole(false);
                }
            };
            fetchUserRole();
        }
    }, [userRole, userRoleState]);

    useEffect(() => {
        const checkForBinImages = async () => {
            if (selectedBins.length > 0) {
                try {
                    const requests  = selectedBins.map(binId => axiosInstance.get(`/bin-media/?bin_id=${binId}`));
                    const responses = await Promise.all(requests);
                    setHasBinImages(responses.some(r => r.data.data && r.data.data.length > 0));
                } catch {
                    setHasBinImages(false);
                }
            } else {
                setHasBinImages(false);
            }
        };
        checkForBinImages();
    }, [selectedBins]);

    useEffect(() => {
        if (!contextBins && !hasInitializedBins.current) {
            hasInitializedBins.current = true;
            const fetchBinIds = async () => {
                try {
                    const response       = await axiosInstance.get("bins-with-status");
                    const binsWithStatus = response.data || [];
                    const validBinIds    = binsWithStatus.map(bin => bin.id).filter(id => id && typeof id === "string");
                    setLocalAllBins(binsWithStatus);
                    setDefaultBinsIfEmpty(validBinIds.slice(0, Math.min(3, validBinIds.length)));
                } catch (error) {
                    console.error("Error fetching bin IDs:", error);
                    try {
                        const fallbackResponse = await axiosInstance.get("bin_ids");
                        const binIds = (fallbackResponse.data || []).filter(id => id && typeof id === "string");
                        setLocalAllBins(binIds.map(id => ({id, status: 'offline'})));
                        setDefaultBinsIfEmpty(binIds.slice(0, Math.min(3, binIds.length)));
                    } catch (fallbackError) {
                        console.error("Error with fallback bin fetch:", fallbackError);
                    }
                }
            };
            fetchBinIds();
        }
    }, [contextBins]);

    useEffect(() => {
        const fetchBinUsageIds = async () => {
            try {
                const queryParams        = selectedBins.length > 0 ? `?bins=${selectedBins.join(',')}` : '';
                const response           = await axiosInstance.get(`bin_usage_ids${queryParams}`, {withCredentials: true});
                const nonGarbyVisionBins = response.data || [];
                setBinUsageIds(nonGarbyVisionBins);
                setShowBinUsageStats(nonGarbyVisionBins.length > 0);
            } catch {
                setShowBinUsageStats(false);
                setBinUsageIds([]);
            }
        };
        if (selectedBins.length > 0) fetchBinUsageIds();
        else { setShowBinUsageStats(false); setBinUsageIds([]); }
    }, [selectedBins]);

    useEffect(() => {
        const refreshBinStatuses = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                setLocalAllBins(response.data || []);
            } catch (error) {
                console.error("Error refreshing bin statuses:", error);
            }
        };
        const interval = setInterval(refreshBinStatuses, 60000);
        return () => clearInterval(interval);
    }, []);

    const selectLocalTimeRange = (timeRange) => {
        setLocalSelectedTimeRange(timeRange);
        return false;
    };

    const toggleLocalWasteTypeSelection = (wasteType) => {
        setLocalSelectedWasteTypes(prev =>
            prev.includes(wasteType) ? prev.filter(item => item !== wasteType) : [...prev, wasteType]
        );
        return false;
    };

    const handleUploadSuccess = () => setRefreshKey(prev => prev + 1);

    return (
        <DashboardExport contaminationData={contaminationExportData}>
            <div className="export-wrapper">
                <div className="tab-content h-full">
                    <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">

                        {!contextTimeRange && (
                            <div className="sticky top-16 pt-4 pb-6 text-right z-10 w-full ml-auto px-4 sm:pr-0 bg-gradient-to-t from-white/0 to-white dark:from-slate-950/0 dark:to-slate-950 flex gap-2 justify-end">
                                <SiteHeader
                                    timeRangeDropdownOpen={localTimeRangeDropdownOpen}
                                    setTimeRangeDropdownOpen={setLocalTimeRangeDropdownOpen}
                                    binDropdownOpen={localBinDropdownOpen}
                                    setBinDropdownOpen={setLocalBinDropdownOpen}
                                    selectedTimeRange={localSelectedTimeRange}
                                    selectTimeRange={selectLocalTimeRange}
                                    timeRangeOptions={timeRangeOptions}
                                    selectedBins={selectedBins}
                                    allBins={localAllBins}
                                    toggleBinSelection={persistentToggleBinSelection}
                                    wasteTypeDropdownOpen={localWasteTypeDropdownOpen}
                                    setWasteTypeDropdownOpen={setLocalWasteTypeDropdownOpen}
                                    selectedWasteTypes={localSelectedWasteTypes}
                                    toggleWasteTypeSelection={toggleLocalWasteTypeSelection}
                                    wasteTypeOptions={wasteTypeOptions}
                                    customDateRange={localCustomDateRange}
                                    isCustomDateRange={localIsCustomDateRange}
                                    onCustomDateRangeChange={handleCustomDateRangeChange}
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-5 gap-3 sm:gap-8 sm:mt-4 font-sans">
                            <div className="mb-3 sm:mb-0 flex flex-col gap-3 sm:gap-8">
                                {selectedBins.length > 0 && hasGarbyVisionBins && (() => {
                                    const allowedImageBins = selectedBins.filter(b => binTypes[b]?.showContaminationImages);
                                    const canSeeContaminationImages = isCurrentUserAdmin || (showContaminationImagesFlag && allowedImageBins.length > 0);
                                    return (
                                        <div className="sm:rounded-lg border-t sm:border shadow-sm bg-muted/50">
                                            <ContaminationChart
                                                title={t('contamination.title')}
                                                timeRange={
                                                    selectedTimeRange.toLowerCase().replace(/\s+/g, "") === "3month"
                                                        ? "3months"
                                                        : selectedTimeRange.toLowerCase().replace(/\s+/g, "") === "6month"
                                                            ? "6months"
                                                            : selectedTimeRange.toLowerCase().replace(/\s+/g, "")
                                                }
                                                bins={selectedBins}
                                                customDateRange={customDateRange}
                                                isCustomDateRange={isCustomDateRange}
                                                canSeeImages={canSeeContaminationImages}
                                                onDataLoaded={handleContaminationDataLoaded}
                                            />
                                        </div>
                                    );
                                })()}

                                <div className="sm:rounded-lg border-t sm:border shadow-sm">
                                    <WasteCreated
                                        bins={selectedBins}
                                        timeRange={selectedTimeRange}
                                        onKgWasteUpdate={setWasteCreatedKg}
                                        customDateRange={customDateRange}
                                        isCustomDateRange={isCustomDateRange}
                                    />
                                </div>

                                <div className="sm:rounded-lg border-t sm:border shadow-sm">
                                    <div className="aspect-[16/10]">
                                        <div className="relative w-full overflow-auto">
                                            <DataTable
                                                className="w-full caption-bottom text-sm aspect-[16/10]"
                                                title={t("filllevellabel")}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 lg:col-span-2 2xl:col-span-4 gap-3 sm:gap-4 content-start items-start">

                                <Stats
                                    className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    title={t("wastegenerationlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                {selectedBins.length > 0 && (
                                    <div
                                        className="lg:col-span-2 lg:row-span-2 sm:rounded-lg bg-muted/50 overflow-hidden flex flex-col"
                                        style={{aspectRatio: window.innerWidth < 640 ? '16/14' : '16/23'}}
                                    >
                                        <SubclassCardView
                                            className="w-full h-full"
                                            id={selectedBins}
                                            dayvalue={selectedTimeRange.toLowerCase()}
                                            datevalue={customDateRange}
                                            isCustomDateRange={isCustomDateRange}
                                            wasteweight={wasteCreatedKg}
                                            totalwasteproduceddata={1004}
                                            isAdmin={isCurrentUserAdmin}
                                        />
                                    </div>
                                )}

                                {selectedBins.length > 0 && (
                                    <SubclassDisplay
                                        className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                        id={selectedBins}
                                        currentWasteItem={selectedWasteTypes[0] || 'organic'}
                                        currentWasteItem_={selectedWasteTypes[0] || 'organic'}
                                        dayvalue={selectedTimeRange.toLowerCase()}
                                        selectedButton={selectedTimeRange.toLowerCase()}
                                        datevalue={customDateRange}
                                        wasteweight={wasteCreatedKg}
                                        isCustomDateRange={isCustomDateRange}
                                    />
                                )}

                                {selectedBins.length > 0 && hasBinImages && (
                                    <BinMediaViewer
                                        key={refreshKey}
                                        selectedBin={selectedBins}
                                        className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    />
                                )}

                                <WasteStreamProduction
                                    className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    title={t("wastestreamproductionlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <PeakUsageTime
                                    className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    title={t("peakUsageTime")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <FillLevelConsistency
                                    className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    title={t("fillLevelConsistency.title")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <BinUsageRanking
                                    className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    title={t("binUsageRanking.defaultTitle")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <WasteComposition
                                    className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    title={t("wastecompositionlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                {selectedBins.length > 0 && hasGarbyVisionBins && (
                                    <ContaminationRateChart
                                        title={t('contaminationRateChart.defaultTitle')}
                                        bins={selectedBins}
                                        timeRange={selectedTimeRange}
                                        customDateRange={customDateRange}
                                        isCustomDateRange={isCustomDateRange}
                                        className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                    />
                                )}

                                {(() => {
                                    const allowedBins = selectedBins.filter(b => binTypes[b]?.showContaminationTime);
                                    const allowedImageBins = selectedBins.filter(b => binTypes[b]?.showContaminationTimeImages);
                                    const canSeeContaminationTimeImages = showContaminationTimeImagesFlag && allowedImageBins.length > 0;
                                    return allowedBins.length > 0 && hasGarbyVisionBins && showContaminationTimeFlag && (
                                        <ContaminationTime
                                            className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                            title="Contamination Time"
                                            timeRange={selectedTimeRange}
                                            bins={allowedBins}
                                            customDateRange={customDateRange}
                                            isCustomDateRange={isCustomDateRange}
                                            userEmail={currentUserEmail}
                                            canSeeImages={canSeeContaminationTimeImages}
                                        />
                                    );
                                })()}

                                {(() => {
                                    const allowedBins = selectedBins.filter(b => binTypes[b]?.showContaminationTimeSeries);
                                    return allowedBins.length > 0 && hasGarbyVisionBins && showContaminationTimeSeriesFlag && (
                                        <ContaminationTimeSeries
                                            title="Contamination Rate Over Time"
                                            bins={allowedBins}
                                            timeRange={selectedTimeRange}
                                            customDateRange={customDateRange}
                                            isCustomDateRange={isCustomDateRange}
                                            className="lg:col-span-2 sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-3 sm:p-4"
                                        />
                                    );
                                })()}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardExport>
    );
}